import { useCallback, useEffect, useMemo, useState } from "react";
import { Cctv, Plus, Image as ImageIcon, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Card } from "@/components/ui/card";
import { PageHeader } from "@/components/layout/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import CctvDetailDialog from "@/components/cctv/CctvDetailDialog";
import { CCTV } from "@/constants/testIds";

const STATUS_VARIANT = {
    draft: "secondary", submitted: "default", with_ops: "warning",
    ops_approved: "success", returned_to_monitor: "destructive",
};

const SEV_VARIANT = {
    low: "secondary", medium: "warning", high: "brown", critical: "destructive",
};

export default function CctvPage() {
    const { t, lang, tn } = useI18n();
    const [scope, setScope] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [listing, setListing] = useState(false);
    const [branch, setBranch] = useState("all");
    const [category, setCategory] = useState("all");
    const [severity, setSeverity] = useState("all");
    const [status, setStatus] = useState("all");
    const [date, setDate] = useState("");
    const [dialog, setDialog] = useState({ open: false, findingId: null });

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/cctv/scope");
                setScope(r.data);
            } catch (e) {
                toast.error(e?.response?.data?.detail || t("error_generic"));
            } finally {
                setLoading(false);
            }
        })();
    }, [t]);

    const loadFindings = useCallback(async () => {
        setListing(true);
        try {
            const params = {};
            if (branch !== "all") params.branch_id = branch;
            if (category !== "all") params.category = category;
            if (severity !== "all") params.severity = severity;
            if (status !== "all") params.status = status;
            if (date) params.date = date;
            const r = await api.get("/cctv/findings", { params });
            setRows(r.data || []);
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setListing(false);
        }
    }, [branch, category, severity, status, date, t]);

    useEffect(() => {
        if (!scope) return;
        loadFindings();
    }, [scope, loadFindings]);

    const hasFilters = branch !== "all" || category !== "all" || severity !== "all" || status !== "all" || !!date;
    const clearFilters = () => { setBranch("all"); setCategory("all"); setSeverity("all"); setStatus("all"); setDate(""); };

    const openNew = () => setDialog({ open: true, findingId: "new" });
    const openRow = (id) => setDialog({ open: true, findingId: id });
    const closeDialog = () => setDialog({ open: false, findingId: null });

    const catLabel = (c) => (c === "other" ? t("cctv_cat_other") : t(`cctv_cat_${c}`));
    const sevLabel = (s) => t(`cctv_sev_${s}`);

    const controls = (
        <>
            <Select value={branch} onValueChange={setBranch}>
                <SelectTrigger data-testid={CCTV.filterBranch} className="w-full sm:w-44"><SelectValue placeholder={t("cctv_filter_branch")} /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">{t("cctv_all")}</SelectItem>
                    {(scope?.branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{tn(b)}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={category} onValueChange={setCategory}>
                <SelectTrigger data-testid={CCTV.filterCategory} className="w-full sm:w-40"><SelectValue placeholder={t("cctv_filter_category")} /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">{t("cctv_all")}</SelectItem>
                    {(scope?.categories || []).map((c) => <SelectItem key={c} value={c}>{catLabel(c)}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={severity} onValueChange={setSeverity}>
                <SelectTrigger data-testid={CCTV.filterSeverity} className="w-full sm:w-36"><SelectValue placeholder={t("cctv_filter_severity")} /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">{t("cctv_all")}</SelectItem>
                    {(scope?.severities || ["low", "medium", "high", "critical"]).map((s) => <SelectItem key={s} value={s}>{sevLabel(s)}</SelectItem>)}
                </SelectContent>
            </Select>
            <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid={CCTV.filterStatus} className="w-full sm:w-40"><SelectValue placeholder={t("cctv_filter_status")} /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">{t("cctv_all")}</SelectItem>
                    {(scope?.statuses || []).map((s) => <SelectItem key={s} value={s}>{t(`cctv_status_${s}`)}</SelectItem>)}
                </SelectContent>
            </Select>
            <Input data-testid={CCTV.filterDate} type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-full sm:w-40" />
            {hasFilters ? (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1.5">
                    <X className="h-3.5 w-3.5" />{t("qc_v_clear_filters")}
                </Button>
            ) : null}
        </>
    );

    const primary = scope?.can_create ? (
        <Button data-testid={CCTV.newBtn} onClick={openNew} className="gap-1.5">
            <Plus className="h-4 w-4" />{t("cctv_new_finding")}
        </Button>
    ) : null;

    if (loading) return <div className="p-4"><Skeleton className="h-80 w-full" /></div>;

    return (
        <div data-testid={CCTV.page} className="space-y-5">
            <PageHeader
                icon={Cctv}
                title={t("cctv_title")}
                subtitle={t("cctv_subtitle")}
                primaryAction={primary}
                actions={controls}
            />

            {rows.length === 0 && !listing ? (
                <EmptyState testId={CCTV.empty} icon={Cctv} title={t("cctv_no_findings")} hint={t("cctv_no_findings_hint")} dataset="cctv_findings" />
            ) : (
                <Card className="rounded-2xl border-[color:var(--border-soft)] shadow-[var(--shadow-sm)] overflow-hidden">
                    <div className="overflow-x-auto scrollbar-thin">
                        <Table data-testid={CCTV.table}>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="text-start whitespace-nowrap">{t("cctv_col_branch")}</TableHead>
                                    <TableHead className="text-start whitespace-nowrap">{t("cctv_col_incident")}</TableHead>
                                    <TableHead className="text-start whitespace-nowrap">{t("cctv_col_category")}</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">{t("cctv_f_severity")}</TableHead>
                                    <TableHead className="text-start whitespace-nowrap">{t("cctv_col_employee")}</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">{t("cctv_col_evidence")}</TableHead>
                                    <TableHead className="text-center whitespace-nowrap">{t("cctv_col_status")}</TableHead>
                                    <TableHead className="text-start whitespace-nowrap">{t("cctv_col_updated")}</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((f) => (
                                    <TableRow
                                        key={f.id}
                                        data-testid={CCTV.row(f.id)}
                                        onClick={() => openRow(f.id)}
                                        className="cursor-pointer"
                                    >
                                        <TableCell className="whitespace-nowrap font-medium text-[color:var(--ink)] text-start">
                                            {tn({ name_ar: f.branch_name_ar, name_en: f.branch_name_en })}
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-start">
                                            <span className="inline-flex flex-col">
                                                <bdi dir="ltr" className="tnum font-medium text-[color:var(--ink)]">{f.incident_date} · {f.incident_time}</bdi>
                                                {f.incident_day ? <span className="text-[11px] text-[color:var(--ink-faint)]">{f.incident_day}</span> : null}
                                            </span>
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-start text-[color:var(--ink)]">
                                            {catLabel(f.category)}
                                            {f.category === "other" && f.category_other ? <span className="text-[color:var(--ink-faint)]"> · {f.category_other}</span> : null}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge data-testid={CCTV.severityBadge(f.id)} variant={SEV_VARIANT[f.severity] || "secondary"}>{sevLabel(f.severity || "medium")}</Badge>
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-start text-[color:var(--ink-muted)]">
                                            {f.employee_name || <span className="italic text-[color:var(--ink-faint)]">{t("cctv_emp_none")}</span>}
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <span className="inline-flex items-center gap-1 text-xs text-[color:var(--ink-muted)]">
                                                <ImageIcon className="h-3.5 w-3.5 text-[color:var(--brand-brown)]" />
                                                <span className="tnum font-semibold">{f.evidence_count || 0}</span>
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-center">
                                            <Badge variant={STATUS_VARIANT[f.status] || "secondary"}>{t(`cctv_status_${f.status}`)}</Badge>
                                        </TableCell>
                                        <TableCell className="whitespace-nowrap text-start text-[11px] text-[color:var(--ink-faint)]">
                                            <bdi dir="ltr">{(f.updated_at || "").slice(0, 16).replace("T", " ")}</bdi>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </Card>
            )}

            <CctvDetailDialog
                open={dialog.open}
                findingId={dialog.findingId}
                scope={scope}
                onClose={closeDialog}
                onSaved={loadFindings}
            />
        </div>
    );
}
